import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Space, Form } from 'antd';

/**
 * 根據給定的模式參數返回相對應的antd Row組件。
 *
 * @param {{mode: ('bottom' | 'modal'), children: React.ReactNode}} props -
 *  - mode: 字符串參數。當值為 'bottom' 時，用於Row內小於三個Col的場景；當值為 'modal' 時，用於modal內。
 *    其餘情形不需傳入此參數。
 *  - children: React元素節點，將被渲染在Row組件內。
 *
 * @return 返回根據mode設置的Row組件。
 *
 * @example
 * // 在Row內小於三個Col的情況下使用
 * <GutteredRow mode="bottom">{...}</GutteredRow>
 *
 * // 在modal內使用
 * <GutteredRow mode="modal">{...}</GutteredRow>
 */
const GutteredRow = ({ mode, children }) => {
  if (mode === 'bottom') {
    //Row內小於三個Col時
    return (
      <Row gutter={16} align="bottom">
        {children}
      </Row>
    );
  } else if (mode === 'modal') {
    return (
      <Row gutter={16} className="common-modal-content">
        {children}
      </Row>
    );
  } else {
    return <Row gutter={16}>{children}</Row>;
  }
};
GutteredRow.propTypes = {
  mode: PropTypes.string,
  children: PropTypes.node,
};

/**
 * 根據給定的參數設定不同寬度的Col組件。
 *
 * @param {{spanFraction: (1 | 2 | 3 | 4), type: ('buttonGroup1' | 'buttonGroup2' | 'buttonGroup3'),display: string, children: React.ReactNode}} props -
 *  - spanFraction: 數字參數，決定Col組件的寬度。可接受的值為1（整行寬）、2（整行的1/2）、3（整行的1/3）或4（整行的1/4）。
 *  - type: 字符串參數，定義特定的按鈕組佈局。可接受的值為'buttonGroup1'（Row內小於三個Col時）、'buttonGroup2'（獨立一列且靠右的按鈕組，通常用於查詢區）或'buttonGroup3'（獨立一列且靠右的按鈕組，用於modal footer）。
 *  - display: 可傳入className，會掛在渲染出的Col上。
 *  - children: React元素節點，將被渲染在Col組件內。
 *
 * @return 返回根據spanFraction和type參數設定的Col組件。
 *
 * @example
 * // 使用整行寬的Col
 * <CustomWidthCol spanFraction={1}>{...}</CustomWidthCol>
 *
 * // 使用整行的1/2寬的Col
 * <CustomWidthCol spanFraction={2}>{...}</CustomWidthCol>
 *
 * // 使用'buttonGroup1'型別的Col
 * <CustomWidthCol type="buttonGroup1">{...}</CustomWidthCol>
 */
const CustomWidthCol = ({ spanFraction, type, display, children }) => {
  if (spanFraction === 1) {
    //Col寬度為整行寬
    return (
      <Col xs={24} sm={24} md={24} lg={24} className={display}>
        {children}
      </Col>
    );
  } else if (spanFraction === 2) {
    //Col寬度為整行的1/2
    return (
      <Col xs={12} sm={12} md={12} lg={12} className={display}>
        {children}
      </Col>
    );
  } else if (spanFraction === 3) {
    //Col寬度為整行的1/3
    return (
      <Col xs={24} sm={24} md={8} lg={8} className={display}>
        {children}
      </Col>
    );
  } else if (spanFraction === 4) {
    //Col寬度為整行的1/4
    return (
      <Col xs={24} sm={24} md={6} lg={6} className={display}>
        {children}
      </Col>
    );
  } else if (type === 'buttonGroup1') {
    //Row裡面小於三個Col時的buttonGroup
    return (
      <Col className={display}>
        <Form.Item>
          <Space>{children}</Space>
        </Form.Item>
      </Col>
    );
  } else if (type === 'buttonGroup2') {
    //獨立一列且靠右的buttonGroup(通常for查詢區)
    return (
      <Col span={24} className={display}>
        <Row justify="end">
          <Col>
            <Form.Item>
              <Space>{children}</Space>
            </Form.Item>
          </Col>
        </Row>
      </Col>
    );
  } else if (type === 'buttonGroup3') {
    //獨立一列且靠右的buttonGroup(for modal footer)
    return (
      <Col span={24} className={display}>
        <Row justify="end">
          <Col>
            <Space>{children}</Space>
          </Col>
        </Row>
      </Col>
    );
  } else {
    //預設Col寬度為整行的1/3
    return (
      <Col xs={24} sm={24} md={8} lg={8} className={display}>
        {children}
      </Col>
    );
  }
};
CustomWidthCol.propTypes = {
  spanFraction: PropTypes.number,
  type: PropTypes.string,
  display: PropTypes.string,
  children: PropTypes.node,
};

export { GutteredRow, CustomWidthCol };
